// client/src/scheduling/utils/aiJobPicker.js
// The grid modal's "AI" job picker (Real | AI | Custom): BMSS AI (00007-101) project jobs,
// ranked for the person being scheduled. Pure — SchedulerGrid owns the jobs + WIP rows.
//
// Sections, top to bottom (a job appears once, in the first section that claims it):
//   1. charged by the scheduled staff in the last AI_RECENT_DAYS, most recent first
//   2. the scheduled staff is the job's JM or JP
//   3. everything else, grouped by PE job class (AI Applications, AI Skills & Plug-Ins, ...)
// With no staff in play (placeholder mode, nobody picked yet) it is just the class groups.

import { isAiJob } from './schedConstants';

export const AI_RECENT_DAYS = 90;

// Every word must appear somewhere, in any order: "1040 tax" finds
// "TAX-AI Project - 1040 Reconciliation". Safe here because the list is already AI-only;
// for the full Real search a short word like "ai" would match inside "Maintenance".
export function matchesAllWords(haystack, query) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = String(haystack || '').toLowerCase();
  return words.every(w => hay.includes(w));
}

const byName = (a, b) => (a.Name || '').localeCompare(b.Name || '');
const fmtHours = (h) => `${Math.round(h * 10) / 10}h`;
const fmtMonthDay = (iso) => { const [, m, d] = iso.split('-'); return `${+m}/${+d}`; };

// jobs: the grid's job list (any jobs; non-AI are ignored). wipRows: rprs.JobsWIP rows for
// the AI client ({ JobId, StaffId, TotalHours, LastDate }). staffIds: who is being scheduled.
// cutoffISO: 'YYYY-MM-DD', the start of the recent window.
// Returns [{ key, label, items: [{ job, hint }] }], empty sections dropped.
export function buildAiJobSections({ jobs, query, staffIds = [], staffLabel = '', wipRows = [], cutoffISO }) {
  const ai = (jobs || []).filter(j => isAiJob(j) && matchesAllWords(
    `${j.Name} ${j.Code} ${j.JobClassDesc || ''} ${j.ManagerName || ''} ${j.StatusDetail || ''}`, query));
  const ids = new Set(staffIds.filter(Boolean));
  const sections = [];
  const taken = new Set();

  if (ids.size) {
    // Lifetime hours per job across the scheduled staff, kept only if any of them charged
    // to it inside the window (LastDate is the only per-staff recency signal on JobsWIP).
    const recent = new Map();
    for (const r of wipRows || []) {
      if (!ids.has(r.StaffId) || !(r.TotalHours > 0)) continue;
      const last = String(r.LastDate || '').slice(0, 10);
      const cur = recent.get(r.JobId) || { hours: 0, last: '' };
      cur.hours += r.TotalHours;
      if (last > cur.last) cur.last = last;
      recent.set(r.JobId, cur);
    }
    const recentItems = ai
      .filter(j => { const r = recent.get(j.JobId); return r && r.last >= cutoffISO; })
      .sort((a, b) => recent.get(b.JobId).last.localeCompare(recent.get(a.JobId).last) || byName(a, b))
      .map(j => { const r = recent.get(j.JobId); return { job: j, hint: `${fmtHours(r.hours)} · last ${fmtMonthDay(r.last)}` }; });
    recentItems.forEach(it => taken.add(it.job.JobId));
    sections.push({ key: 'recent', label: `Charged by ${staffLabel} · last ${AI_RECENT_DAYS} days`, items: recentItems });

    const leadItems = ai
      .filter(j => !taken.has(j.JobId) && (ids.has(j.ManagerId) || ids.has(j.PartnerId)))
      .sort(byName)
      .map(j => ({ job: j, hint: ids.has(j.ManagerId) ? 'JM' : 'JP' }));
    leadItems.forEach(it => taken.add(it.job.JobId));
    sections.push({ key: 'lead', label: `${staffLabel} is JM / JP`, items: leadItems });
  }

  const byClass = new Map();
  for (const j of ai) {
    if (taken.has(j.JobId)) continue;
    const cls = j.JobClassDesc || 'Other';
    if (!byClass.has(cls)) byClass.set(cls, []);
    byClass.get(cls).push(j);
  }
  [...byClass.keys()]
    .sort((a, b) => (a === 'Other') - (b === 'Other') || a.localeCompare(b))
    .forEach(cls => sections.push({
      key: `class:${cls}`, label: cls, items: byClass.get(cls).sort(byName).map(job => ({ job, hint: null })),
    }));

  return sections.filter(s => s.items.length > 0);
}
